/**
 * UI Overlays slice — tracks which "blocking" right-drawers / popovers are
 * currently open across the app. Owners (e.g. SelectPatientRightDrawer,
 * AddPatientRightDrawer, ConsentRightDrawer) register themselves via the
 * `useRegisterOverlay` hook when they mount-open and unregister on close /
 * unmount.
 *
 * Why this exists:
 *   The Noki Copilot launcher panel auto-collapses while another overlay is
 *   on screen so the user isn't fighting two stacked sheets. Without a
 *   central registry the launcher would need to subscribe to N booleans
 *   scattered across N parents — fragile and not scalable. One slice +
 *   one hook keeps that coupling additive.
 *
 * What this is NOT:
 *   - Not the copilot's own panel state (that stays local to NokiCopilot).
 *   - Not a routing / modal stack (overlays don't push/pop history).
 *   - Not coupled to the recording mute path (that still lives on
 *     `state.recognizer.recordingByEncounter`).
 *
 * Shape: `{ openIds: string[] }` — order = registration order. Order is
 * not meaningful, but stable so devtools history reads naturally.
 */

import { createSlice, createSelector } from "@reduxjs/toolkit";

/**
 * Transient "peek" overlay id used when a voice command changes a Default
 * System Setting dropdown (conversation language, clinical note language,
 * watermark, ...). Registering it briefly collapses the copilot panel so
 * the user can SEE the dropdown update underneath; NokiCopilot reopens the
 * panel ("peek-and-return") when this id clears. Unlike the drawer ids,
 * this one auto-reopens — see NokiCopilot's peek effect.
 */
export const COPILOT_SETTINGS_PEEK_OVERLAY_ID = "copilot_settings_peek";

const initialState = {
  openIds: [],
};

const uiOverlaysSlice = createSlice({
  name: "uiOverlays",
  initialState,
  reducers: {
    /**
     * Mark an overlay as open. Idempotent — registering the same id twice
     * is a no-op so React StrictMode double-invokes can't corrupt state.
     */
    registerOverlay: (state, action) => {
      const id = action.payload;
      if (typeof id !== "string" || id.length === 0) return;
      if (state.openIds.includes(id)) return;
      state.openIds.push(id);
    },
    /**
     * Mark an overlay as closed. Idempotent — unregistering an id that is
     * not currently open is a no-op.
     */
    unregisterOverlay: (state, action) => {
      const id = action.payload;
      if (typeof id !== "string" || id.length === 0) return;
      state.openIds = state.openIds.filter((existing) => existing !== id);
    },
    /**
     * Escape hatch for crash-recovery / logout flows. Clears every
     * tracked overlay. Not used by the normal open/close cycle.
     */
    resetOverlays: (state) => {
      state.openIds = [];
    },
  },
});

export const { registerOverlay, unregisterOverlay, resetOverlays } =
  uiOverlaysSlice.actions;

// --- Selectors --------------------------------------------------------------
// Keep these alongside the slice so consumers don't poke the shape directly.

const selectOpenIds = (state) => state?.uiOverlays?.openIds ?? [];

export const selectAnyOverlayOpen = createSelector(
  [selectOpenIds],
  (ids) => ids.length > 0,
);

export const selectFirstOpenOverlayId = createSelector(
  [selectOpenIds],
  (ids) => (ids.length > 0 ? ids[0] : null),
);

export const selectIsOverlayOpen = (id) =>
  createSelector([selectOpenIds], (ids) => ids.includes(id));

export default uiOverlaysSlice.reducer;
